import { useState } from "react";
import { ArchiveRestore, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PERMISSIONS } from "@/constants/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BillingTranslations } from "@/lib/i18n/types/billing";
import { cn } from "@/lib/utils";
import { formatBillingDate, formatCompactBillingPeriod, nextInvoiceDate, plural } from "../format";
import { useBillingMoney, useBillingOverview, useRestoreBillingBuilding } from "../hooks";
import { billingErrorMessage } from "../messages";
import type { BilledBuilding, BillingContext, BillingOverview, RemovedBuilding } from "../types";
import { AddBuildingDialog } from "./AddBuildingDialog";
import { useBuildingCostConsent } from "../useBuildingCostConsent";
import { CurrentPeriodBuildings } from "./CurrentPeriodBuildings";
import { RemoveBuildingDialog } from "./RemoveBuildingDialog";
import { RestrictedActionButton } from "./RestrictedActionButton";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

const BILLING_SUMMARY_GRID = "grid-cols-[2.2fr_1fr_1fr]";

type Restrictions = BillingTranslations["restrictions"];

function OverviewLoading({ label }: { label: string }) {
  return (
    <div className="space-y-6" aria-label={label}>
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

// Every billing mutation is org_owner-only server-side (the `billing` edge
// function refuses anything else with `not_owner`), so the owner check comes
// before the module permission — an org admin holding ADD_DATA would otherwise
// get an enabled button and a 403.
function addRestriction(context: BillingContext, canAdd: boolean, copy: Restrictions) {
  if (!context.is_org_owner) return copy.ownerOnly;
  if (!canAdd) return copy.noPermission;
  return undefined;
}

// READ-ONLY DELIBERATELY DOES NOT APPEAR HERE. Removing a building is the one
// lever that lowers the bill, and the server keeps it open on purpose:
// `refuseAction` gates only `add_building` / `restore_building` on read_only,
// because refusing removal "would punish the organisation for the state it is
// trying to leave". Withholding it in the UI would leave a locked-out
// organisation unable to stop the charge that locked it out — and say so with a
// reason the backend does not hold.
//
// `canDelete` is therefore the billing-EXEMPT grant (`holdsPermission`), not
// `canDeleteData`: DELETE_DATA is in BILLING_BLOCKED_MUTATIONS, so reading the
// gated boolean would re-impose the same block one message later.
function removeRestriction(context: BillingContext, canDelete: boolean, copy: Restrictions) {
  if (!context.is_org_owner) return copy.ownerOnly;
  if (!canDelete) return copy.noPermission;
  return undefined;
}

// The three states the SERVER refuses a restore in, kept together because they
// are one rule — `refuseAction` gates restore_building on each of them.
function restoreBillingRestriction(context: BillingContext, copy: Restrictions) {
  if (context.is_read_only) return copy.restoreReadOnly;
  if (context.details_missing) return copy.restoreDetailsMissing;
  if (context.payment_method_missing) return copy.restorePaymentMethodMissing;
  return undefined;
}

function restoreRestriction(
  context: BillingContext,
  building: RemovedBuilding,
  canAdd: boolean,
  pending: boolean,
  isRestoring: boolean,
  copy: Restrictions
) {
  if (pending) return isRestoring ? copy.restoring : copy.restorePending;
  const refusedByBilling = restoreBillingRestriction(context, copy);
  if (refusedByBilling) return refusedByBilling;
  if (!building.can_restore) return copy.restoreExpired;
  if (!context.is_org_owner) return copy.ownerOnly;
  if (!canAdd) return copy.noPermission;
  return undefined;
}

function RateValue({
  label,
  value,
  isRate = false,
}: {
  label: string;
  value: React.ReactNode;
  isRate?: boolean;
}) {
  return (
    <div className={cn("px-3 py-4 sm:py-5 lg:px-6", isRate && "relative")}>
      {isRate ? (
        <span aria-hidden="true" className="absolute inset-y-0 left-0 w-[3px] bg-primary" />
      ) : null}
      <p className="mb-1.5 whitespace-nowrap text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          isRate ? "text-xl font-semibold leading-snug tracking-tight" : "text-sm font-medium",
          "whitespace-nowrap"
        )}
      >
        {value}
      </p>
    </div>
  );
}

function RateInformation({ overview }: { overview: BillingOverview }) {
  const context = overview.context;
  const { t } = useLanguage();
  const money = useBillingMoney();
  // The rate alone decides, because a NULL `negotiated_rate_valid_until` is an
  // OPEN-ENDED contract rate, not an absent one — `private.org_billing_rate_cents`
  // reads `valid_until IS NULL OR >= period_start`. Requiring both fields hid
  // this block from exactly the organisations paying a negotiated price
  // indefinitely, leaving the one screen that explains why they are not paying
  // the list rate silent about it.
  if (context.negotiated_rate_cents === null) return null;
  if (
    overview.buildings.length > 0 &&
    overview.buildings.every((building) => building.subscription_plan_billing_enabled === true)
  )
    return null;

  return (
    <section className="pb-6">
      <p className="mb-3 text-xs text-muted-foreground">{t.billing.plans.legacyAgreement}</p>
      <div
        className={cn(
          "grid divide-x divide-border overflow-hidden rounded-md border border-border",
          BILLING_SUMMARY_GRID
        )}
      >
        <RateValue
          label={t.billing.rate.agreedRate}
          isRate
          value={
            <>
              <span className="whitespace-nowrap">
                {money(context.negotiated_rate_cents, context.currency)}
              </span>{" "}
              <span className="whitespace-nowrap text-sm font-medium tracking-normal">
                {t.billing.rate.perBuildingMonth}
              </span>
            </>
          }
        />
        <RateValue
          label={t.billing.rate.agreedUntil}
          value={
            context.negotiated_rate_valid_until
              ? formatBillingDate(context.negotiated_rate_valid_until)
              : t.billing.rate.openEnded
          }
        />
        <RateValue label={t.billing.rate.contact} value={context.billing_contact_name ?? "—"} />
      </div>
    </section>
  );
}

function SummaryValue({
  label,
  value,
  detail,
  prominent = false,
}: {
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
  prominent?: boolean;
}) {
  return (
    <div className="min-w-0 space-y-1 rounded-xl bg-neutral-50 px-3 py-4 lg:px-5 lg:py-5">
      <p className="min-h-8 text-xs font-medium uppercase tracking-wider text-muted-foreground lg:min-h-0">
        {label}
      </p>
      <p
        className={cn(
          "text-lg font-semibold lg:text-2xl",
          prominent && "tracking-tight",
          prominent && "whitespace-nowrap"
        )}
      >
        {value}
      </p>
      {detail ? (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">{detail}</div>
      ) : null}
    </div>
  );
}

function BillingSummary({ overview }: { overview: BillingOverview }) {
  const { t } = useLanguage();
  const money = useBillingMoney();
  const copy = t.billing.summary;

  return (
    <section className="pb-10" aria-label={copy.currentPeriod}>
      <div className="grid grid-cols-[1.15fr_1fr_1.15fr] gap-3 lg:gap-4">
        <SummaryValue
          label={copy.currentPeriod}
          prominent
          value={formatCompactBillingPeriod(overview.period_start, overview.period_end)}
          detail={fill(plural(copy.closesIn_one, copy.closesIn_other, overview.closes_in_days), {
            days: String(overview.closes_in_days),
          })}
        />
        <SummaryValue label={copy.buildings} value={overview.building_count} />
        <SummaryValue
          label={fill(copy.renewalOn, { date: nextInvoiceDate(overview.period_end) })}
          value={
            overview.renewal_total_cents === undefined
              ? "—"
              : money(overview.renewal_total_cents, overview.context.currency)
          }
        />
      </div>
    </section>
  );
}

interface RemovedBuildingRowProps {
  building: RemovedBuilding;
  context: BillingContext;
  canAdd: boolean;
  pending: boolean;
  activeBuildingId?: number;
  restore: (buildingId: number) => void;
}

function RemovedBuildingRow(props: RemovedBuildingRowProps) {
  const { t } = useLanguage();
  const copy = t.billing.removed;
  const isRestoring = props.pending && props.activeBuildingId === props.building.building_id;
  const reason = restoreRestriction(
    props.context,
    props.building,
    props.canAdd,
    props.pending,
    isRestoring,
    t.billing.restrictions
  );

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell>
        <p className="font-medium">{props.building.building_name}</p>
        <p className="text-xs text-muted-foreground">
          {fill(copy.removedOn, { date: formatBillingDate(props.building.removed_on) })}
        </p>
      </TableCell>
      <TableCell>{formatBillingDate(props.building.usable_until)}</TableCell>
      <TableCell>{formatBillingDate(props.building.deletes_on)}</TableCell>
      <TableCell className="text-right">
        <RestrictedActionButton
          {...pageInfoTarget("billing.restore")}
          variant="ghost"
          size="sm"
          disabledReason={reason}
          onClick={() => props.restore(props.building.building_id)}
        >
          {isRestoring ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ArchiveRestore className="h-4 w-4" />
          )}
          {copy.restore}
        </RestrictedActionButton>
      </TableCell>
    </TableRow>
  );
}

function RemovedBuildings(
  props: Omit<RemovedBuildingRowProps, "building"> & { buildings: RemovedBuilding[] }
) {
  const { t } = useLanguage();
  const copy = t.billing.removed;
  if (!props.buildings.length) return null;

  return (
    <section className="space-y-4 py-10 first:pt-0">
      <SettingsSectionLabel>{copy.sectionTitle}</SettingsSectionLabel>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="font-medium tracking-wider">{copy.building}</TableHead>
            <TableHead className="font-medium tracking-wider">{copy.usableUntil}</TableHead>
            <TableHead className="font-medium tracking-wider">{copy.deletedOn}</TableHead>
            <TableHead>
              <span className="sr-only">{t.billing.table.action}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {props.buildings.map((building) => (
            <RemovedBuildingRow key={building.building_id} {...props} building={building} />
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

function TransferSupport() {
  const { t } = useLanguage();
  return (
    <section className="py-10 first:pt-0 last:pb-0">
      <div className="flex items-start gap-3 rounded-xl bg-neutral-50 p-4 text-muted-foreground">
        <Info aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <p className="text-sm">
          {t.billing.transfer.before}{" "}
          <a
            className="font-medium text-primary underline-offset-4 hover:underline"
            href="mailto:support@frnkln.ai"
          >
            support@frnkln.ai
          </a>{" "}
          {t.billing.transfer.after}
        </p>
      </div>
    </section>
  );
}

function useRestoreAction(orgId: string) {
  const { t } = useLanguage();
  const mutation = useRestoreBillingBuilding();
  // The same agreement the Buildings page asks for before an un-archive, and the
  // same component: a restore in a later period is a fresh prorated add.
  const consent = useBuildingCostConsent();
  const restore = async (buildingId: number) => {
    if (!(await consent.request({ mode: "restore", buildingId }))) return;
    try {
      await mutation.mutateAsync({ orgId, buildingId });
      toast.success(t.billing.removed.restored);
    } catch (error) {
      toast.error(billingErrorMessage(error, t, t.billing.removed.restoreFailed));
    }
  };
  return { mutation, restore, dialog: consent.dialog };
}

function BillingOverviewContent({
  overview,
  refreshing,
}: {
  overview: BillingOverview;
  refreshing: boolean;
}) {
  const { t } = useLanguage();
  const permissions = usePermissions();
  const restoreAction = useRestoreAction(overview.context.org_id);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [buildingToRemove, setBuildingToRemove] = useState<BilledBuilding | null>(null);
  const restrictions = t.billing.restrictions;
  const addDisabledReason = addRestriction(
    overview.context,
    permissions.holdsPermission(PERMISSIONS.CREATE_BUILDINGS),
    restrictions
  );

  return (
    <>
      <div>
        <RateInformation overview={overview} />
        <BillingSummary overview={overview} />
        <div className="divide-y divide-border">
          <CurrentPeriodBuildings
            overview={overview}
            addDisabledReason={addDisabledReason}
            removeDisabledReason={removeRestriction(
              overview.context,
              permissions.holdsPermission(PERMISSIONS.DELETE_DATA),
              restrictions
            )}
            chooseForRemoval={setBuildingToRemove}
            openAdd={() => setIsAddOpen(true)}
          />
          <RemovedBuildings
            buildings={overview.removed_buildings}
            context={overview.context}
            canAdd={permissions.canAddData}
            pending={restoreAction.mutation.isPending}
            activeBuildingId={restoreAction.mutation.variables?.buildingId}
            restore={restoreAction.restore}
          />
          <TransferSupport />
        </div>
      </div>

      {restoreAction.dialog}

      <AddBuildingDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        orgId={overview.context.org_id}
        quote={overview.add_building_quote}
        currency={overview.context.currency}
        context={overview.context}
        quoteRefreshing={refreshing}
      />
      <RemoveBuildingDialog
        building={buildingToRemove}
        orgId={overview.context.org_id}
        periodEnd={overview.period_end}
        currency={overview.context.currency}
        onOpenChange={(open) => {
          if (!open) setBuildingToRemove(null);
        }}
      />
    </>
  );
}

export function BillingOverviewPage() {
  const { t } = useLanguage();
  const query = useBillingOverview();
  if (query.isLoading) return <OverviewLoading label={t.billing.page.overviewTitle} />;
  if (query.isError || !query.data) {
    return (
      <div className="border-y border-destructive/40 py-6">
        <p className="font-medium">{t.billing.errors.overviewTitle}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t.billing.errors.overviewBody}</p>
        <Button className="mt-4" variant="outline" onClick={() => query.refetch()}>
          {t.billing.errors.retry}
        </Button>
      </div>
    );
  }
  return <BillingOverviewContent overview={query.data} refreshing={query.isFetching} />;
}
